/**
 * Stats Dashboard formatters.
 *
 * These helpers all accept a numeric value (or `null`/`undefined`) and
 * return a localised, human-friendly string. They prefer the user's
 * organisation locale where possible, falling back to `nl-NL` so the
 * dashboard stays consistent with the rest of the SmartFlow app.
 */

const DEFAULT_LOCALE = 'nl-NL';
const DEFAULT_CURRENCY = 'EUR';

const isNullish = (v) => v == null || (typeof v === 'number' && Number.isNaN(v));

/**
 * Currency with no fractional digits (`€42.500`).
 *
 * Backend totals are decimals, but for KPI cards the cents are noise —
 * the only place we care about cents is the average ticket, where a
 * separate helper renders 2 decimals.
 */
export const formatCurrency = (
  value,
  { locale = DEFAULT_LOCALE, currency = DEFAULT_CURRENCY } = {}
) => {
  if (isNullish(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Currency with explicit precision — for "avg ticket" type KPIs where
 * 2 decimals matter, or for tooltips on the revenue timeline.
 */
export const formatCurrencyPrecise = (
  value,
  { locale = DEFAULT_LOCALE, currency = DEFAULT_CURRENCY } = {}
) => {
  if (isNullish(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Compact currency for chart axis labels: `€12K`, `€2,4M`.
 *
 * `Intl.NumberFormat` does this natively with `notation: 'compact'`.
 */
export const formatCurrencyCompact = (
  value,
  { locale = DEFAULT_LOCALE, currency = DEFAULT_CURRENCY } = {}
) => {
  if (isNullish(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

/**
 * Plain integer (`42`).
 */
export const formatInteger = (value, { locale = DEFAULT_LOCALE } = {}) => {
  if (isNullish(value)) return '—';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Decimal number with explicit precision (`146,5`).
 */
export const formatDecimal = (value, { locale = DEFAULT_LOCALE, fractionDigits = 1 } = {}) => {
  if (isNullish(value)) return '—';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

/**
 * Percentage where the input is already in percent points (e.g. 63.4
 * means "63.4%"). The backend always sends pre-multiplied values, so
 * we don't divide by 100.
 */
export const formatPercent = (value, { locale = DEFAULT_LOCALE, fractionDigits = 1 } = {}) => {
  if (isNullish(value)) return '—';
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}%`;
};

/**
 * Days as a localised number with optional precision.
 * "34.2 d" / "12,8 dagen" depending on caller-provided suffix.
 */
export const formatDays = (
  value,
  { locale = DEFAULT_LOCALE, fractionDigits = 1, suffix = 'd' } = {}
) => {
  if (isNullish(value)) return '—';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
  return suffix ? `${formatted} ${suffix}` : formatted;
};

/**
 * Hours, with localised decimals and a unit suffix. Mirrors how
 * `installer-stats` returns `logged_hours: 146.5`.
 */
export const formatHours = (
  value,
  { locale = DEFAULT_LOCALE, fractionDigits = 1, suffix = 'u' } = {}
) => {
  if (isNullish(value)) return '—';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
  return suffix ? `${formatted}${suffix}` : formatted;
};

/**
 * Format a backend bucket-key (`YYYY-MM-DD`) according to the chosen
 * timeline interval. Daily buckets show `12 mei`, weekly buckets show
 * `wk 19` ("week 19"), monthly buckets show `mei 2026`.
 */
export const formatBucketLabel = (bucket, interval, { locale = DEFAULT_LOCALE } = {}) => {
  if (!bucket) return '';
  const date = new Date(`${bucket}T00:00:00`);
  if (Number.isNaN(date.getTime())) return bucket;

  const intl = (opts) => new Intl.DateTimeFormat(locale, opts).format(date);

  switch (interval) {
    case 'monthly':
      return intl({ month: 'short', year: 'numeric' });
    case 'weekly': {
      const weekNumber = getISOWeek(date);
      return `wk ${weekNumber}`;
    }
    case 'daily':
    default:
      return intl({ day: 'numeric', month: 'short' });
  }
};

/**
 * ISO 8601 week number — Monday-based, week 1 contains the first
 * Thursday of the year. Vanilla `Intl` doesn't expose this directly,
 * so we calculate it ourselves.
 */
export const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

/**
 * Short range label "12 apr — 12 mei" used in the page header.
 */
export const formatPeriodRange = (start, end, { locale = DEFAULT_LOCALE } = {}) => {
  if (!start || !end) return '';
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '';
  }
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const startStr = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
  }).format(startDate);
  const endStr = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(endDate);
  return `${startStr} — ${endStr}`;
};

/**
 * Signed percent-points string with a Δ arrow. Used by the margin
 * widgets to render `margin_pct_delta` (e.g. `▲ +4.9 pp`, `▼ -1.2 pp`).
 *
 * Returns `'—'` when the value is null/undefined — the backend uses
 * `null` for "no previous period" or "previous revenue was 0".
 */
export const formatPercentPointsDelta = (
  value,
  { locale = DEFAULT_LOCALE, fractionDigits = 1 } = {}
) => {
  if (isNullish(value)) return '—';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: 'exceptZero',
  }).format(value);
  if (value > 0) return `▲ ${formatted} pp`;
  if (value < 0) return `▼ ${formatted} pp`;
  return `${formatted} pp`;
};

/**
 * Signed percent string with a Δ arrow (e.g. `▲ +26.3 %`,
 * `▼ -12.4 %`). Used for the trend strip — current vs. previous —
 * on the margin totals tile.
 *
 * Backend clamps `*_pct` trends to 100% when the previous value is 0
 * (everything-from-nothing growth), so we don't have to worry about
 * Infinity here.
 */
export const formatPercentDelta = (value, { locale = DEFAULT_LOCALE, fractionDigits = 1 } = {}) => {
  if (isNullish(value)) return '—';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: 'exceptZero',
  }).format(value);
  if (value > 0) return `▲ ${formatted}%`;
  if (value < 0) return `▼ ${formatted}%`;
  return `${formatted}%`;
};

/**
 * Format a margin coverage percentage. We bias the precision down
 * for legibility — coverage of 0.16% is meaningful (it tells you
 * "almost no data yet"), but 0.157% is just noise.
 */
export const formatCoveragePct = (value, { locale = DEFAULT_LOCALE } = {}) => {
  if (isNullish(value)) return '—';
  // Use 1 decimal for sub-10%, 0 for the rest — keeps "0.2%" precise
  // while showing "97%" instead of "97.0%".
  const fractionDigits = Math.abs(value) < 10 ? 1 : 0;
  return formatPercent(value, { locale, fractionDigits });
};

/**
 * Short, locale-aware date label (`12 mei 2026`) — used by the
 * under-threshold list to render `accepted_at`.
 */
export const formatShortDate = (value, { locale = DEFAULT_LOCALE } = {}) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/**
 * "30s ago" / "5m ago" / "2h ago" relative timestamp for the
 * per-widget "last updated" hint. Falls back to a date string for
 * stale enough values.
 */
export const formatRelative = (timestamp, { now = Date.now() } = {}) => {
  if (!timestamp) return '';
  const diff = Math.max(0, now - timestamp);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 5) return 'zojuist';
  if (seconds < 60) return `${seconds}s geleden`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m geleden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.floor(hours / 24);
  return `${days}d geleden`;
};
